import { format as formatDateFns, isValid, parse } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

// Helper function to format date string to YYYY-MM-DD
export const formatDateForInput = (dateStr?: string | null): string => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().split('T')[0]
}

// Convert ISO date (YYYY-MM-DD) to display format without timezone issues
export const formatIsoDateToDisplay = (
  isoDate?: string | null,
  lang = 'fr'
): string => {
  if (!isoDate) return ''

  // Parse the ISO date string (YYYY-MM-DD) manually to avoid timezone issues
  const dateParts = isoDate.split('-')
  if (dateParts.length !== 3) return ''

  const year = parseInt(dateParts[0], 10)
  const month = parseInt(dateParts[1], 10) - 1 // JavaScript months are 0-based
  const day = parseInt(dateParts[2], 10)

  const date = new Date(year, month, day)
  if (Number.isNaN(date.getTime())) return ''

  const formatStr = formatByLang(lang)
  return formatDateFns(date, formatStr)
}

// Convert display date to ISO format (YYYY-MM-DD) without timezone issues
export const formatDisplayDateToIso = (displayDate: Date): string => {
  if (!displayDate || Number.isNaN(displayDate.getTime())) return ''

  // Use local date methods to avoid timezone issues
  const year = displayDate.getFullYear()
  const month = String(displayDate.getMonth() + 1).padStart(2, '0')
  const day = String(displayDate.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const useDateInput = () => {
  const { i18n } = useTranslation()
  const lang = i18n.language
  const regex = regexByLang(lang)
  const mask = maskByLang(lang)
  const placeholder = placeholderByLang(lang)
  const locale = dateFnsLocaleByAppLocale(lang)
  const formatStr = formatByLang(lang)
  const format = (date: Date | undefined) => {
    if (!date) return undefined
    return formatDateFns(date, formatStr)
  }
  const validateDate = (value: string | undefined) => {
    if (!value) return false
    if (!regex.test(value)) return false
    const date = parse(value, formatStr, new Date(), { locale })

    return isValid(date)
  }

  const getDate = (value: string | undefined) => {
    if (!value) return undefined
    if (!validateDate(value)) return undefined
    const date = parse(value, formatStr, new Date(), { locale })

    return date
  }

  return { format, regex, mask, placeholder, getDate, validateDate }
}

const formatByLang = (lang: string) => {
  switch (lang) {
    case 'fr':
      return 'dd/MM/yyyy'
    default: // en
      return 'yyyy/MM/dd'
  }
}

const maskByLang = (lang: string) => {
  switch (lang) {
    case 'fr':
      return '99/99/9999'
    default: // en
      return '9999/99/99'
  }
}

const placeholderByLang = (lang: string) => {
  switch (lang) {
    case 'fr':
      return '__/__/____'
    default: // en
      return '____/__/__'
  }
}

const regexByLang = (lang: string) => {
  switch (lang) {
    case 'fr':
      return /^\d{2}\/\d{2}\/\d{4}$/
    default: // en
      return /^\d{4}\/\d{2}\/\d{2}$/
  }
}

export const dateFnsLocaleByAppLocale = (lang: string) => {
  switch (lang) {
    case 'fr':
      return fr
    default: // en
      return enUS
  }
}

/** Formats time string (HH:mm) to a natural label (e.g. 09:00 -> 9h, 13:30 -> 13h30). */
export function formatTimeLabel(time: string): string {
  const [h, m] = time.split(':')
  const hour = parseInt(h, 10)
  const min = parseInt(m, 10)
  if (min === 0) {
    return `${hour}h`
  }
  return `${hour}h${String(min).padStart(2, '0')}`
}
